import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useOrg } from "../lib/org";
import {
  Button,
  ErrorState,
  Field,
  Modal,
  Money,
  PageHeader,
  Spinner,
  TextField,
} from "../components/ui";
import { DataTable } from "../components/DataTable";
import type { Customer } from "../lib/models";

export default function Customers() {
  const { currentOrg } = useOrg();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const currency = currentOrg?.baseCurrency ?? "USD";

  const { data, isLoading, error } = useQuery({
    queryKey: ["customers", currentOrg?.id],
    queryFn: () => api.get<Customer[]>("/customers"),
    enabled: !!currentOrg,
  });

  const sync = useMutation({
    mutationFn: (id: string) => api.post(`/customers/${id}/sync`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["customers"] }),
  });

  return (
    <div>
      <PageHeader
        title="Customers"
        subtitle="People and businesses you invoice"
        action={<Button onClick={() => setOpen(true)}>New customer</Button>}
      />
      {isLoading && <Spinner />}
      {error && <ErrorState error={error} />}
      {data && (
        <DataTable
          rows={data}
          rowKey={(r) => r.id}
          columns={[
            {
              key: "name",
              header: "Name",
              render: (r) => (
                <div>
                  <div className="font-medium text-gray-900">{r.displayName}</div>
                  {r.companyName && (
                    <div className="text-xs text-gray-400">{r.companyName}</div>
                  )}
                </div>
              ),
            },
            { key: "email", header: "Email", render: (r) => r.email ?? "—" },
            {
              key: "ext",
              header: "org_customer_id",
              render: (r) =>
                r.orgCustomerId ? (
                  <span className="font-mono text-xs text-gray-600">
                    {r.orgCustomerId}
                  </span>
                ) : (
                  <span className="text-gray-300">—</span>
                ),
            },
            {
              key: "balance",
              header: "Open balance",
              align: "right",
              render: (r) => <Money cents={r.balance} currency={currency} />,
            },
            {
              key: "actions",
              header: "",
              align: "right",
              render: (r) =>
                r.orgCustomerId ? (
                  <Button
                    variant="ghost"
                    onClick={() => sync.mutate(r.id)}
                    disabled={sync.isPending}
                  >
                    Sync ↻
                  </Button>
                ) : null,
            },
          ]}
        />
      )}

      <NewCustomerModal
        open={open}
        onClose={() => setOpen(false)}
        onCreated={() => {
          setOpen(false);
          qc.invalidateQueries({ queryKey: ["customers"] });
        }}
      />
    </div>
  );
}

function NewCustomerModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [form, setForm] = useState({
    displayName: "",
    companyName: "",
    email: "",
    phone: "",
    orgCustomerId: "",
    notes: "",
  });
  const create = useMutation({
    mutationFn: () =>
      api.post("/customers", {
        displayName: form.displayName,
        companyName: form.companyName || undefined,
        email: form.email || undefined,
        phone: form.phone || undefined,
        orgCustomerId: form.orgCustomerId || undefined,
        notes: form.notes || undefined,
      }),
    onSuccess: onCreated,
  });

  return (
    <Modal open={open} onClose={onClose} title="New customer">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <Field label="Display name">
          <TextField
            required
            value={form.displayName}
            onChange={(e) => setForm({ ...form, displayName: e.target.value })}
          />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Company">
            <TextField
              value={form.companyName}
              onChange={(e) => setForm({ ...form, companyName: e.target.value })}
            />
          </Field>
          <Field label="Email">
            <TextField
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Phone">
            <TextField
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </Field>
          <Field
            label="org_customer_id"
            hint="Link to your external system (64-bit)"
          >
            <TextField
              inputMode="numeric"
              value={form.orgCustomerId}
              onChange={(e) =>
                setForm({
                  ...form,
                  orgCustomerId: e.target.value.replace(/[^0-9]/g, ""),
                })
              }
            />
          </Field>
        </div>
        {create.error && <ErrorState error={create.error} />}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? "Saving…" : "Create customer"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
